import { isValid, parseISO } from "date-fns";

export interface WorkflowInfoApi {
  workflow_config_uuid: string;
  name: string;
}

export interface WorkflowRun {
  id?: number;
  external_id?: string;
  created_at?: Date;
  modified_at?: Date;
  is_deleted?: boolean;
  modified_by?: string;
  workflow_config_id?: number;
  name?: string;
  started_at?: Date;
  finished_at?: Date;
  payload?: any;
  status?: string;
  reviewed?: boolean;
  due_date?: string;
  due_date_updated?: boolean;
  error_type?: string;
  error_message?: string;
  locked_by?: string;
  lock_expire?: Date;
  company_uuid?: string;
  parent_company_uuid?: string;
  blueprint_uuid?: string;
  blueprint_snapshot_uuid?: string;
  behavior_kit_uuid?: string;
  last_running_step?: string;
  sla_met?: boolean;
  requested_by?: string;
  computed_blueprint?: any;
  parent_workflow_run_id?: number;
  computed_workflow_config?: any;
  is_invalid?: boolean;
}

export interface WorkflowStepRun {
  id: number;
  external_id: string;
  created_at: string;
  modified_at: string;
  is_deleted: boolean;
  modified_by: string;
  workflow_run_id: number;
  workflow_run_uuid: string;
  workflow_step_config_id: number;
  name: string;
  event_id: string;
  started_at: string;
  finished_at: string;
  expired_at: string;
  retry_nb: number;
  payload: {
    email: string;
    full_name: string;
    person_uuid: string;
    report_uuid: string;
    company_name: string;
    company_uuid: string;
    report_types: string[];
    requested_by: string;
    blueprint_name: string;
    blueprint_uuid: string;
    blueprint_names: string[];
    client_report_id: string;
    behavior_kit_name: string;
    behavior_kit_uuid: null | string;
    parent_workflow_run_id: number;
    rerun_from_parent_uuid: string;
    parent_workflow_run_uuid: string;
    parent_workflow_step_run_id: number;
    parent_workflow_step_run_uuid: string;
  };
  last_running_step: string;
  status: string;
  error_type: null | string;
  error_message: null | string;
}

export interface WorkflowRunEx extends WorkflowRun {
  step_runs?: WorkflowStepRun[];
}

export type WorkflowRunExPage = [WorkflowRunEx[], number];

export interface WorkflowRunExPageRequest {
  search?: string;
  page?: number;
  page_size?: number;
  individual?: boolean;
}

export interface WorkflowRunChangeDueDate {
  external_id?: string;
  due_date?: Date;
}

export interface WorkflowDashboardEntry {
  date: Date;
  past_due: boolean;
  step_name: WORKFLOW_STEP_STATUS_ENUM | null;
  count: number;
}

export interface ThresholdDashboardEntry {
  days: number;
  step_name?: WORKFLOW_STEP_STATUS_ENUM | null;
  threshold: number;
  modified_by?: string;
}

export enum WORKFLOW_STATUS_ENUM {
  TO_DO = "TO_DO",
  IN_PROGRESS = "IN_PROGRESS",
  DONE = "DONE",
  FAILED = "FAILED",
  TIMEOUT = "TIMEOUT",
  CANCELLED = "CANCELLED",
}

export enum WORKFLOW_MANUAL_SCRAPE_STATUS {
  IN_PROGRESS = "IN_PROGRESS",
  DONE = "DONE",
  SKIPPED = "SKIPPED",
}

export interface WorkflowBase {
  external_id?: string;
  is_deleted: boolean;
  name: string;
  trigger: string;
  expression: string;
}

export enum WORKFLOW_STEP_STATUS_ENUM {
  PersonScrape = "PersonScrape",
  ProfilesModelPredict = "ProfilesModelPredict",
  ProfilesAutoRate = "ProfilesAutoRate",
  ProfilesManualRate = "ProfilesManualRate",
  PersonEnrichment = "PersonEnrichment",
  PostsManualScrape = "PostsManualScrape",
  ProfilePostScrape = "ProfilePostScrape",
  KeywordMatch = "KeywordMatch",
  PostsModelPredict = "PostsModelPredict",
  PostsAutoRate = "PostsAutoRate",
  PostsManualRate = "PostsManualRate",
  InternalQA = "InternalQA",
  ClientQA = "ClientQA",
  PostsManualEdit = "PostsManualEdit",
  FinalReportQA = "FinalReportQA",
  PDFGeneration = "PDFGeneration",
  WebContentReview = "WebContentReview",
  ArticlesManualEdit = "ArticlesManualEdit",
  WebContentScrape = "WebContentScrape",
  InstagramPostScrape = "InstagramPostScrape",
  XPostScrape = "XPostScrape",
  TiktokPostScrape = "TiktokPostScrape",
  LinkedinPostScrape = "LinkedinPostScrape",
  FacebookPostScrape = "FacebookPostScrape",
  YouTubePostScrape = "YouTubePostScrape",
  RedditPostScrape = "RedditPostScrape",
  BlueskyPostScrape = "BlueskyPostScrape",
  InstantFit = "InstantFit",
  Misconduct = "Misconduct",
  CompleteReport = "CompleteReport",
  AmendmentQA = "AmendmentQA",
  ThreeSixtyAmend = "360Amend",
  ThreeSixtyQA = "360QA",
  ThreeSixtyReview = "360Review",
  ThreeSixtyPlusReview = "360PlusReview",
  ThreeSixtyWebSearch = "360WebSearch",
  ThreeSixtySubworkflow = "360",
}

export interface StatusBucket {
  step_names: (null | WORKFLOW_STEP_STATUS_ENUM)[];
  name: string;
  is_manual: boolean;
}

export const WorkflowStatusBucket: Array<StatusBucket> = [
  {
    step_names: [
      WORKFLOW_STEP_STATUS_ENUM.PersonScrape,
      WORKFLOW_STEP_STATUS_ENUM.ProfilesAutoRate,
      WORKFLOW_STEP_STATUS_ENUM.ProfilesModelPredict,
      WORKFLOW_STEP_STATUS_ENUM.PersonEnrichment,
    ],
    name: "System Profile Confirm",
    is_manual: false,
  },
  {
    step_names: [WORKFLOW_STEP_STATUS_ENUM.WebContentScrape],
    name: "Web Content Scrape",
    is_manual: false,
  },
  {
    step_names: [WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate],
    name: "Manual Profile Confirm",
    is_manual: true,
  },
  {
    step_names: [WORKFLOW_STEP_STATUS_ENUM.WebContentReview],
    name: "Web Content Review",
    is_manual: true,
  },
  {
    step_names: [
      WORKFLOW_STEP_STATUS_ENUM.ProfilePostScrape,
      WORKFLOW_STEP_STATUS_ENUM.InstagramPostScrape,
      WORKFLOW_STEP_STATUS_ENUM.XPostScrape,
      WORKFLOW_STEP_STATUS_ENUM.TiktokPostScrape,
      WORKFLOW_STEP_STATUS_ENUM.LinkedinPostScrape,
      WORKFLOW_STEP_STATUS_ENUM.FacebookPostScrape,
      WORKFLOW_STEP_STATUS_ENUM.YouTubePostScrape,
      WORKFLOW_STEP_STATUS_ENUM.RedditPostScrape,
      WORKFLOW_STEP_STATUS_ENUM.BlueskyPostScrape,
    ],
    name: "System Post Scrape",
    is_manual: false,
  },
  {
    step_names: [WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape],
    name: "Manual Post Scrape",
    is_manual: true,
  },
  {
    step_names: [
      WORKFLOW_STEP_STATUS_ENUM.KeywordMatch,
      WORKFLOW_STEP_STATUS_ENUM.PostsModelPredict,
      WORKFLOW_STEP_STATUS_ENUM.PostsAutoRate,
    ],
    name: "System Post Predict",
    is_manual: false,
  },
  {
    step_names: [WORKFLOW_STEP_STATUS_ENUM.PostsManualRate],
    name: "Manual Post Rate",
    is_manual: true,
  },
  {
    step_names: [WORKFLOW_STEP_STATUS_ENUM.InternalQA],
    name: "Internal QA",
    is_manual: true,
  },
];

export interface WorkflowStepsUI {
  name: string;
  color: string;
  textColor?: string;
}

const WORKFLOW_STEP_UI_MAP = {
  [WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate]: {
    name: "Manual Profile Confirm",
    color: "bg-amber-100",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.ProfilePostScrape]: {
    name: "System Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape]: {
    name: "Manual Post Scrape",
    color: "bg-violet-100",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PostsModelPredict]: {
    name: "Post Predict",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.KeywordMatch]: {
    name: "Keyword Matching",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PostsManualRate]: {
    name: "Manual Post Rate",
    color: "bg-blue-100",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.WebContentReview]: {
    name: "Web Content Review",
    color: "bg-fuchsia-100",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.WebContentScrape]: {
    name: "Web Content Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.InstagramPostScrape]: {
    name: "Instagram Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.XPostScrape]: {
    name: "X Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.LinkedinPostScrape]: {
    name: "Linkedin Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.FacebookPostScrape]: {
    name: "Facebook Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.TiktokPostScrape]: {
    name: "TikTok Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.ProfilesModelPredict]: {
    name: "Profiles Model Predict",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PDFGeneration]: {
    name: "PDF Generation",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PersonScrape]: {
    name: "Person Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.ProfilesAutoRate]: {
    name: "Profiles Auto Rate",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PersonEnrichment]: {
    name: "Person Enrichment",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PostsAutoRate]: {
    name: "Posts Auto Rate",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.PostsManualEdit]: {
    name: "Posts Manual Edit",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.FinalReportQA]: {
    name: "Final Report QA",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.ArticlesManualEdit]: {
    name: "Articles Manual Edit",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.YouTubePostScrape]: {
    name: "YouTube Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.RedditPostScrape]: {
    name: "Reddit Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.BlueskyPostScrape]: {
    name: "Bluesky Post Scrape",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.AmendmentQA]: {
    name: "Manual Profile Edit",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend]: {
    name: "360 Amend",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.InternalQA]: {
    name: "Internal QA",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.ClientQA]: {
    name: "Client QA",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.InstantFit]: {
    name: "Instant Fit",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.Misconduct]: {
    name: "Misconduct",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
  [WORKFLOW_STEP_STATUS_ENUM.CompleteReport]: {
    name: "Complete Report",
    color: "bg-gray-300",
    textColor: "text-gray-900",
  },
} as const;

/** Returns true if the backend step name has a UI mapping (for WebSocket step updates). */
export function isStepNameInUiMap(stepName: string): boolean {
  return Object.prototype.hasOwnProperty.call(WORKFLOW_STEP_UI_MAP, stepName);
}

/**
 * Normalize backend step name (e.g. snake_case) to UI PascalCase for WORKFLOW_STEP_UI_MAP.
 * Example: "person_scrape" -> "PersonScrape". Returns unchanged if already PascalCase.
 */
export function normalizeStepNameForUi(stepName: string): string {
  if (!stepName || stepName.includes(" ")) return stepName;
  if (isStepNameInUiMap(stepName)) return stepName;
  return stepName
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

export function workStepNameToUI(
  workflowStepName: WORKFLOW_STEP_STATUS_ENUM
): WorkflowStepsUI | undefined {
  return (
    WORKFLOW_STEP_UI_MAP[
      workflowStepName as keyof typeof WORKFLOW_STEP_UI_MAP
    ] || {
      name: workflowStepName,
      color: "bg-green-300",
      textColor: "text-gray-900",
    }
  );
}

export function uiNameToWorkflowStep(
  uiName: string
): WORKFLOW_STEP_STATUS_ENUM | undefined {
  const reverseMap: { [key: string]: WORKFLOW_STEP_STATUS_ENUM } = {};
  Object.entries(WORKFLOW_STEP_UI_MAP).forEach(([stepName, uiInfo]) => {
    reverseMap[uiInfo.name] = stepName as WORKFLOW_STEP_STATUS_ENUM;
  });
  reverseMap["Amendment QA"] = WORKFLOW_STEP_STATUS_ENUM.AmendmentQA;
  reverseMap["360 Amend"] = WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend;

  return reverseMap[uiName];
}

export function uiNameToWorkflowStepNames(uiName: string): string {
  const bucket = WorkflowStatusBucket.find((bucket) => bucket.name === uiName);

  if (!bucket) {
    // Fallback in case UI name is not a bucket name.
    const singleStep = uiNameToWorkflowStep(uiName);
    return singleStep || uiName;
  }

  return bucket.step_names
    .filter((name): name is WORKFLOW_STEP_STATUS_ENUM => name !== null)
    .join(",");
}

export interface WorkflowRunRowDataResponse {
  behavior_kit_name?: string;
  blueprint_name?: string | null;
  client_report_id: string;
  company_name?: string | null;
  created_at: string;
  due_date: string;
  due_date_updated: boolean;
  external_id: string;
  full_name?: string | null;
  has_rerun?: boolean;
  is_locked?: boolean;
  locked_by?: string | null;
  lock_expire?: string | null;
  recur: string;
  report_created_at?: string | null;
  report_uuid?: string | null;
  rerun_from_parent_id?: string | null;
  status: `${WORKFLOW_STATUS_ENUM}`;
  steps_in_progress: `${WORKFLOW_STEP_STATUS_ENUM}`[];
  was_qa?: boolean | null;
}

type LockExpireMillis = "missing" | "invalid" | number;

/** Parses lock_expire to epoch ms, or missing / invalid (business rules live in isWorkflowRunVisuallyLocked). */
function lockExpireToMillis(
  exp: string | Date | null | undefined
): LockExpireMillis {
  if (exp == null || exp === "") return "missing";
  if (exp instanceof Date) {
    const t = exp.getTime();
    return Number.isNaN(t) ? "invalid" : t;
  }
  const d = parseISO(exp);
  if (!isValid(d)) return "invalid";
  return d.getTime();
}

/**
 * Row styling for QA lists: `/workflow_run/row_data/` may report is_locked: false while
 * the dashboard list still has locked_by + lock_expire. Merge list row + detail for display.
 */
export function isWorkflowRunVisuallyLocked(row: {
  is_locked?: boolean;
  locked_by?: string | null;
  lock_expire?: string | Date | null | undefined;
}): boolean {
  if (row.is_locked === true) return true;
  if (row.locked_by == null || row.locked_by === "") return false;

  const expMs = lockExpireToMillis(row.lock_expire);
  if (expMs === "missing") return true;
  if (expMs === "invalid") return true;
  return expMs > Date.now();
}

export type WorkflowRunListLockFields = Pick<
  WorkflowRun,
  "locked_by" | "lock_expire"
>;

export function isWorkflowRunVisuallyLockedWithListRow(
  detail: WorkflowRunRowDataResponse | undefined,
  listRow: WorkflowRunListLockFields
): boolean {
  if (!detail) return isWorkflowRunVisuallyLocked(listRow);
  return isWorkflowRunVisuallyLocked({
    is_locked: detail.is_locked,
    locked_by: detail.locked_by ?? listRow.locked_by,
    lock_expire: detail.lock_expire ?? listRow.lock_expire,
  });
}

export interface WorkflowByStepSummaryItem {
  full_name: string;
  due_date: string;
  company_name: string;
  blueprint_name: string;
  report_uuid: string;
  is_locked?: boolean;
  due_date_updated?: boolean;
}

export interface ReportSummaryPagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}
